
import React, { useState, useEffect } from 'react';
import { X, Check, CreditCard, Clock, Star, Zap, Loader2, Calendar, AlertCircle, ExternalLink, ShieldCheck, Sparkles, Mail } from 'lucide-react';
import { supabase, SUPABASE_URL, SUPABASE_KEY } from '../supabase';
import { Subscription, AgentProfile, AppSettings } from '../types';

interface SubscriptionModalProps {
  agent: AgentProfile;
  onClose: () => void;
}

const PLANS = [
  {
    name: 'Basic',
    basePrice: 2000,
    features: [
      'Up to 50 property listings',
      'Auto-renew every 6 hours',
      'Standard visibility',
      'AI Description assistant',
      'Manual listing refresh'
    ],
    color: 'text-gray-400'
  },
  {
    name: 'Pro',
    basePrice: 5000,
    features: [
      'Up to 100 property listings',
      'Auto-renew every 3 hours',
      '10 Premium Listing slots',
      'Priority visibility',
      'Unlimited AI Descriptions',
      'WhatsApp button highlight'
    ],
    color: 'text-blue-500'
  },
  {
    name: 'Elite',
    basePrice: 10000,
    features: [
      'Up to 200 property listings',
      'Auto-renew every 2 hours',
      '40 Premium Listing slots',
      'Top of search results',
      'Featured Listing badges',
      'Custom profile themes',
      'Direct lead notifications'
    ],
    color: 'text-amber-500'
  }
];

const DURATIONS = [
  { months: 1, label: '1 Month', discount: 0 },
  { months: 3, label: '3 Months', discount: 0.1 },
  { months: 6, label: '6 Months', discount: 0.2 },
  { months: 12, label: '1 Year', discount: 0.3 }
];

// Added missing React import to resolve 'Cannot find namespace React'
export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ agent, onClose }) => {
  const [selectedPlan, setSelectedPlan] = useState(PLANS[1]);
  const [selectedDuration, setSelectedDuration] = useState(DURATIONS[0]);
  const [history, setHistory] = useState<Subscription[]>([]);
  const [appSettings, setAppSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [showManualPay, setShowManualPay] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const PIONEER_BONUS_MONTHS = 2;

  useEffect(() => {
    fetchHistory();
    fetchAppSettings();
  }, []);

  const fetchAppSettings = async () => {
    if (!supabase) return;
    try {
      const { data } = await supabase.from('app_settings').select('*').maybeSingle();
      if (data) setAppSettings(data);
    } catch (e) {
      console.warn("Could not fetch app settings.");
    }
  };

  const fetchHistory = async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('agent_id', agent.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setHistory(data || []);
    } catch (err) {
      console.error('Error fetching billing history:', err);
    } finally {
      setFetching(false);
    }
  };

  const handleKoraCheckout = async () => {
    console.log("[Checkout] Starting checkout process...");
    if (!supabase) {
      alert("Checkout Failed: Database connection is not active.");
      return;
    }
    
    setLoading(true);
    setEmailError(null);

    try {
      console.log("[Checkout] Getting Supabase session...");
      const { data: { session } } = await supabase.auth.getSession();
      const authUser = session?.user;
      const userEmail = agent.email || authUser?.email;
      
      console.log("[Checkout] User Email:", userEmail);
      if (!userEmail) {
        setEmailError("Email Required: Please update your email in Profile Settings before paying.");
        setLoading(false);
        return;
      }

      const totalPrice = selectedPlan.basePrice * selectedDuration.months * (1 - selectedDuration.discount);
      const reference = `FINDILY_${Date.now()}_${agent.id.slice(0, 4)}`;
      const finalDurationMonths = selectedDuration.months + PIONEER_BONUS_MONTHS;

      console.log("[Checkout] Creating pending subscription record in DB...");
      const { error: dbError } = await supabase.from('subscriptions').insert([{
        agent_id: agent.id,
        plan_name: selectedPlan.name,
        duration_months: finalDurationMonths,
        price: totalPrice,
        discount: Math.round(selectedDuration.discount * 100),
        status: 'pending',
        is_paid: false,
        payment_method: 'Kora Online',
        payment_reference: reference
      }]);

      if (dbError) {
        console.error("[Checkout] DB Error:", dbError);
        throw new Error(`Database Error: ${dbError.message}`);
      }

      console.log("[Checkout] Preparing Edge Function call...");
      const functionName = 'kora-checkout';
      const redirectUrl = new URL(window.location.origin);
      redirectUrl.searchParams.set('agent', agent.id);
      redirectUrl.searchParams.set('reference', reference);

      const payload = { 
        amount: totalPrice, 
        email: userEmail, 
        reference,
        redirect_url: redirectUrl.toString(),
      };

      const manualUrl = `${SUPABASE_URL}/functions/v1/${functionName}`;
      console.log("[Checkout] Calling Edge Function at:", manualUrl);
      
      const authHeader = session?.access_token 
        ? `Bearer ${session.access_token}` 
        : `Bearer ${SUPABASE_KEY}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.warn("[Checkout] Request timed out after 60s");
        controller.abort();
      }, 60000);

      try {
        const manualResponse = await fetch(manualUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authHeader,
            'apikey': SUPABASE_KEY
          },
          body: JSON.stringify(payload),
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        console.log("[Checkout] Edge Function Response Status:", manualResponse.status);

        if (!manualResponse.ok) {
          const errText = await manualResponse.text();
          console.error("[Checkout] Edge Function Error Text:", errText);
          let errorDetail = errText;
          try {
            const parsed = JSON.parse(errText);
            errorDetail = parsed.error || parsed.message || errText;
          } catch (e) {}
          
          if (manualResponse.status === 504 || manualResponse.status === 502) {
            throw new Error("The payment server is currently overloaded or taking too long. Please wait a moment and try again.");
          }
          
          throw new Error(`Payment Initialization Failed (${manualResponse.status}): ${errorDetail}`);
        }

        const responseData = await manualResponse.json();
        console.log("[Checkout] Edge Function Success Data:", responseData);
        
        if (responseData?.checkout_url) {
          console.log("[Checkout] Redirecting to:", responseData.checkout_url);
          window.location.href = responseData.checkout_url;
        } else {
          throw new Error(responseData?.error || "Payment server returned an invalid response.");
        }
      } catch (fetchErr: any) {
        clearTimeout(timeoutId);
        if (fetchErr.name === 'AbortError') {
          throw new Error("Connection Timeout: The request took longer than 60 seconds. This usually happens when the payment provider is slow. Please try again.");
        }
        throw fetchErr;
      }

    } catch (err: any) {
      console.error("Checkout Failure:", err);
      alert(
        `Checkout Error: ${err.message}\n\n` +
        `Troubleshooting:\n` +
        `1. Check Supabase Logs (Edge Functions > kora-checkout > Logs)\n` +
        `2. Ensure KORA_SECRET_KEY is set in Supabase Secrets\n` +
        `3. Ensure "Enforce JWT Verification" is OFF in Function Settings`
      );
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = () => {
    const total = selectedPlan.basePrice * selectedDuration.months * (1 - selectedDuration.discount);
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      maximumFractionDigits: 0
    }).format(total);
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl animate-in fade-in duration-300 overflow-y-auto">
      <div className="bg-white dark:bg-zinc-950 w-full max-w-xl rounded-[32px] shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh]">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="animate-pulse flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-300 fill-amber-300" />
              <span className="text-[10px] font-black uppercase tracking-widest text-white">Pioneer Launch Offer: +2 Months Free!</span>
            </div>
          </div>
          <div className="px-2 py-0.5 bg-white/20 rounded-md text-[8px] font-black text-white uppercase">Active</div>
        </div>

        <div className="p-6 border-b border-gray-100 dark:border-zinc-900 flex justify-between items-center bg-white/50 dark:bg-zinc-950/50 backdrop-blur-md">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-amber-500/10 rounded-xl">
              <Zap className="w-5 h-5 text-amber-500" />
            </div>
            <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight uppercase">Account Upgrade</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-900 rounded-full transition-colors">
            <X className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-8 scrollbar-hide">
          {emailError && (
            <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-2xl flex items-start gap-3 animate-in shake duration-500">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-[11px] font-black text-red-600 dark:text-red-400 uppercase tracking-wider">Email Required</p>
                <p className="text-[10px] text-red-500/80 font-bold leading-relaxed">{emailError}</p>
                <button onClick={onClose} className="text-[10px] font-black text-red-600 uppercase underline mt-2">Go to Settings</button>
              </div>
            </div>
          )}

          {showManualPay && appSettings ? (
            <div className="space-y-6 animate-in slide-in-from-right duration-300">
               <div className="p-6 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20 rounded-[32px] space-y-4">
                  <div className="flex items-center gap-3">
                     <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white"><ShieldCheck /></div>
                     <div>
                        <h4 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight">Manual Transfer</h4>
                        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Verification in 24-48 hours</p>
                     </div>
                  </div>
                  <div className="pt-4 space-y-3 border-t border-blue-100 dark:border-blue-900/30">
                     <div className="flex justify-between items-center">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Bank Name</span>
                        <span className="text-xs font-black dark:text-white">{appSettings.bank_name}</span>
                     </div>
                     <div className="flex justify-between items-center">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Account Number</span>
                        <button 
                          type="button"
                          onClick={() => { 
                            if (appSettings?.account_number) {
                              navigator.clipboard.writeText(appSettings.account_number); 
                              alert("Account number copied!"); 
                            }
                          }} 
                          className="text-xs font-black dark:text-white flex items-center gap-1 hover:text-blue-500"
                        >
                          {appSettings.account_number} <Calendar className="w-3 h-3" />
                        </button>
                     </div>
                     <div className="flex justify-between items-center">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Account Name</span>
                        <span className="text-xs font-black dark:text-white">{appSettings.account_name}</span>
                     </div>
                  </div>
               </div>
               <button onClick={() => setShowManualPay(false)} className="w-full py-4 text-[10px] font-black uppercase tracking-widest text-gray-400">Back to Online Payment</button>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Select Duration</label>
                <div className="flex gap-2 overflow-x-auto pt-5 pb-2 scrollbar-hide">
                  {DURATIONS.map((dur) => (
                    <button
                      key={dur.months}
                      onClick={() => setSelectedDuration(dur)}
                      className={`flex-shrink-0 px-5 py-2.5 rounded-full text-[11px] font-black uppercase tracking-wider transition-all active:scale-95 border-2 relative ${
                        selectedDuration.months === dur.months
                          ? 'bg-black dark:bg-white text-white dark:text-black border-black dark:border-white shadow-lg'
                          : 'bg-transparent text-gray-400 border-gray-100 dark:border-zinc-900 hover:border-gray-300 dark:hover:border-zinc-700'
                      }`}
                    >
                      {dur.label} 
                      {dur.discount > 0 && <span className="text-[9px] ml-1 text-amber-500">-{dur.discount * 100}%</span>}
                      <span className="absolute -top-3 -right-1 bg-blue-500 text-white text-[7px] px-1.5 py-0.5 rounded-full border border-white dark:border-zinc-950 font-black whitespace-nowrap z-10 shadow-sm">
                        +2mo Bonus
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Select Plan</label>
                  <select 
                    className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm appearance-none cursor-pointer border border-transparent hover:border-gray-200 dark:hover:border-zinc-800"
                    value={selectedPlan.name}
                    onChange={(e) => setSelectedPlan(PLANS.find(p => p.name === e.target.value)!)}
                  >
                    {PLANS.map(p => <option key={p.name} value={p.name}>{p.name} Tier</option>)}
                  </select>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">Total Due</p>
                  <p className="text-3xl font-black text-gray-900 dark:text-white tracking-tighter">{calculatePrice()}</p>
                  <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest mt-1">Status: {selectedDuration.months + PIONEER_BONUS_MONTHS} Months Access</p>
                </div>
              </div>

              <div className="p-6 bg-gray-50 dark:bg-zinc-900/50 rounded-[32px] border border-gray-100 dark:border-zinc-900">
                <div className="flex items-center gap-2 mb-4">
                  <Star className={`w-4 h-4 ${selectedPlan.color}`} />
                  <h4 className="text-[11px] font-black uppercase tracking-widest text-gray-900 dark:text-white">{selectedPlan.name} Tier Features</h4>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {selectedPlan.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-green-500" />
                      </div>
                      <span className="text-xs font-bold text-gray-600 dark:text-zinc-400">{f}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <button 
                  onClick={handleKoraCheckout}
                  disabled={loading}
                  className="w-full py-5 bg-blue-600 text-white rounded-2xl font-black text-sm uppercase tracking-[0.2em] shadow-2xl shadow-blue-500/20 hover:bg-blue-700 active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CreditCard className="w-5 h-5" /> Activate Pioneer Plan</>}
                </button>
                <button 
                  onClick={() => setShowManualPay(true)}
                  className="w-full py-4 text-[11px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Prefer Bank Transfer?
                </button>
              </div>

              <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-zinc-900">
                <div className="flex items-center justify-between">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Billing History</h3>
                  <Clock className="w-3.5 h-3.5 text-gray-300" />
                </div>
                
                {fetching ? (
                  <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>
                ) : history.length > 0 ? (
                  <div className="space-y-2">
                    {history.map((sub) => (
                      <div key={sub.id} className="flex items-center justify-between p-4 bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-2xl hover:border-gray-200 dark:hover:border-zinc-700 transition-colors group">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${sub.status === 'active' ? 'bg-green-500/10 text-green-500' : 'bg-gray-100 dark:bg-zinc-800 text-gray-400'}`}>
                            <Calendar className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-[11px] font-black text-gray-900 dark:text-white uppercase tracking-wider">{sub.plan_name} Tier</p>
                            <p className="text-[9px] font-bold text-gray-400 uppercase">{new Date(sub.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-black text-gray-900 dark:text-white">₦{sub.price.toLocaleString()}</p>
                          <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
                            sub.status === 'active' ? 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400' : 
                            sub.status === 'pending' ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400' : 
                            'bg-gray-100 text-gray-500 dark:bg-zinc-800 dark:text-zinc-500'
                          }`}>
                            {sub.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 border-2 border-dashed border-gray-100 dark:border-zinc-900 rounded-[32px]">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">No transaction history yet</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        
        <div className="p-4 bg-gray-50 dark:bg-zinc-900/30 border-t border-gray-100 dark:border-zinc-900 flex items-center gap-3">
           <AlertCircle className="w-4 h-4 text-gray-400" />
           <p className="text-[9px] font-medium text-gray-500 leading-tight">
             Securely activated via Kora. Your account updates automatically upon confirmation.
           </p>
        </div>
      </div>
    </div>
  );
};

const CopyIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);
